import { TFunction } from "i18next";

// Matches the "code"/"params"/"requirements" fields api.ts attaches to a thrown Error
// when the backend sent them (see internal/pseudonymizer/handlers/error_codes.go).
export interface ApiErrorLike {
  code?: string;
  params?: Record<string, unknown>;
  requirements?: { code: string; params?: Record<string, unknown> }[];
}

const PASSWORD_ERROR_CODES = new Set([
  "PASSWORD_REQUIREMENTS_NOT_MET",
  "PASSWORD_FORBIDDEN_PATTERN",
  "PASSWORD_BREACHED",
]);

// Renders an error caught from apiService into the user's current language. Falls back
// to a generic translated message for any code the UI doesn't recognize yet, rather
// than showing the raw English string the backend sends for logs/API consumers.
export function translateApiError(t: TFunction, err: unknown): string {
  const generic = t("apiErrors:generic");
  if (!(err instanceof Error)) {
    return generic;
  }

  const apiErr = err as Error & ApiErrorLike;
  if (!apiErr.code) {
    return generic;
  }

  if (PASSWORD_ERROR_CODES.has(apiErr.code) && apiErr.requirements?.length) {
    const intro = t(`apiErrors:${apiErr.code}`, { defaultValue: generic });
    const lines = apiErr.requirements.map((r) =>
      t(`apiErrors:passwordRequirements.${r.code}`, { ...r.params, defaultValue: r.code }),
    );
    return `${intro}\n- ${lines.join("\n- ")}`;
  }

  return t(`apiErrors:${apiErr.code}`, { ...apiErr.params, defaultValue: generic });
}
